import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Case, LandTransporter } from '../../types';
import { DroppableZone, DraggableCase } from './';
import { calculateCasePosition, TIME_SLOT_WIDTH } from '../../utils/timeSlotUtils';

/**
 * 陸送員行のプロパティ
 */
export interface TransporterRowOptimizedProps {
  /**
   * 陸送員データ
   */
  transporter: LandTransporter;

  /**
   * 表示する時間スロット配列
   */
  timeSlots: string[];

  /**
   * その日の全案件リスト
   */
  cases: Case[];

  /**
   * 選択された曜日
   */
  selectedDayOfWeek: keyof LandTransporter['workingDays'];

  /**
   * 選択された日付（YYYY-MM-DD形式）
   */
  selectedDate: string;

  /**
   * ドラッグオーバー中の陸送員
   */
  dragOverTransporter: LandTransporter | null;

  /**
   * ドロップ可能かどうか
   */
  canDrop: boolean;

  /**
   * ドロップ不可の理由
   */
  dropReason: string;

  /**
   * 現在ドラッグ中の案件
   */
  activeCase: Case | null;

  /**
   * 案件未割り当てハンドラー
   */
  onUnassignCase: (caseId: string) => void;

  /**
   * 陸送員クリックハンドラー
   */
  onTransporterClick: (transporter: LandTransporter) => void;

  /**
   * 車両名取得関数
   */
  getVehicleName: (case_: Case) => string;

  /**
   * 請求先名取得関数
   */
  getBillingPartnerName: (case_: Case) => string;

  /**
   * 引き取り先名取得関数
   */
  getPickupPartnerName: (case_: Case) => string;

  /**
   * 搬入先名取得関数
   */
  getDeliveryPartnerName: (case_: Case) => string;

  /**
   * 時間スロットクリックハンドラー（オプション）
   */
  onTimeSlotClick?: (transporterId: string, timeSlot: string, date: string) => void;

  /**
   * FL-563: 実際の登録スケジュール（driver_schedules）
   * - timeStart: 勤務開始時間（例: "10:00"）
   * - timeEnd: 勤務終了時間（例: "16:00"）
   */
  registeredSchedule?: { timeStart: string; timeEnd: string } | null;
}

/**
 * 陸送員行コンポーネント（最適化版）
 * FL-158: 陸送員スケジュールのレイアウト最適化
 *
 * 陸送員名を左側に固定表示し、右側にタイムテーブルを表示する行形式のレイアウト。
 * スクロール時も陸送員名は固定され、常に誰のスケジュールかを把握可能です。
 */
export const TransporterRowOptimized: React.FC<TransporterRowOptimizedProps> = ({
  transporter,
  timeSlots,
  cases,
  selectedDayOfWeek,
  selectedDate,
  dragOverTransporter,
  canDrop,
  dropReason,
  activeCase,
  onUnassignCase,
  onTransporterClick,
  getVehicleName,
  getBillingPartnerName,
  getPickupPartnerName,
  getDeliveryPartnerName,
  onTimeSlotClick,
  registeredSchedule, // 追加
}) => {
  // 🔧 FL-563: driver_schedules（実登録スケジュール）を優先使用
  const workingHours = registeredSchedule
    ? {
        startTime: registeredSchedule.timeStart,
        endTime: registeredSchedule.timeEnd,
      }
    : transporter.workingDays?.[selectedDayOfWeek] || {
        startTime: '08:00',
        endTime: '18:00',
      };

  // 陸送員に割り当てられた案件を取得
  const assignedCases = cases.filter(case_ => case_.assignedLandTransporterId === transporter.id);

  // レーン配置アルゴリズム：重複しない行に案件を配置（メモ化）
  const assignCasesToLanes = useCallback(
    (cases: Case[]) => {
      const lanes: Array<{ caseId: string; startTime: string; endTime: string }[]> = [];
      const caseToLane: Map<string, number> = new Map();

      // 開始時間でソートして配置の一貫性を保つ
      const sortedCases = [...cases].sort((a, b) =>
        a.pickupTimeStart.localeCompare(b.pickupTimeStart)
      );

      sortedCases.forEach(case_ => {
        const startTime = case_.pickupTimeStart;
        const endTime =
          case_.deliveryDate === selectedDate ? case_.deliveryTime : workingHours.endTime;

        // 既存のレーンで重複しない場所を探す
        let assignedLane = -1;
        for (let i = 0; i < lanes.length; i++) {
          const canFit = lanes[i].every(
            occupant => endTime <= occupant.startTime || startTime >= occupant.endTime
          );
          if (canFit) {
            assignedLane = i;
            break;
          }
        }

        // 新しいレーンが必要な場合
        if (assignedLane === -1) {
          lanes.push([]);
          assignedLane = lanes.length - 1;
        }

        lanes[assignedLane].push({
          caseId: case_.id,
          startTime,
          endTime,
        });

        caseToLane.set(case_.id, assignedLane);
      });

      return { lanes, caseToLane, totalLanes: lanes.length };
    },
    [selectedDate, workingHours.endTime]
  );

  // レーン配置を計算（メモ化）
  const { caseToLane, totalLanes } = useMemo(
    () => assignCasesToLanes(assignedCases),
    [assignCasesToLanes, assignedCases]
  );

  // 総レーン数に基づく案件表示エリアの高さ計算
  const getContainerHeight = (totalLanes: number) => {
    const laneHeight = 56; // 各レーンの高さ
    const topOffset = 2;
    const bottomOffset = 2;
    const minHeight = 56;

    return Math.max(minHeight, topOffset + totalLanes * laneHeight + bottomOffset);
  };

  // レーン番号に基づく垂直位置の計算
  const getLaneStyle = (laneNumber: number) => {
    const laneHeight = 56;
    const topOffset = 2;
    const top = topOffset + laneNumber * laneHeight;
    const height = 52;

    return {
      top: `${top}px`,
      height: `${height}px`,
    };
  };

  // 陸送員の特記事項タグを生成（色付き）
  const getTransporterTags = (
    transporter: LandTransporter
  ): Array<{ text: string; className: string }> => {
    const tags: Array<{ text: string; className: string }> = [];
    if (transporter.canWorkNightShift)
      tags.push({
        text: '夜勤可',
        className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
      });
    if (transporter.canWorkLongDistance)
      tags.push({
        text: '長距離可',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      });
    if (transporter.hasTemporaryLicense)
      tags.push({
        text: '仮ナンバー有',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      });
    if (transporter.isATOnly)
      tags.push({
        text: 'AT限定',
        className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      });
    if (transporter.isSolicitationProhibited)
      tags.push({
        text: '勧誘NG',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      });
    if (transporter.isDesignatedStoreProhibited)
      tags.push({
        text: '指定店NG',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      });
    return tags;
  };

  const transporterTags = getTransporterTags(transporter);
  const containerHeight = getContainerHeight(totalLanes);

  // 左側エリアの高さを測定（ResizeObserverを使用してパフォーマンス最適化）
  const leftSideRef = useRef<HTMLDivElement>(null);
  const [leftSideHeight, setLeftSideHeight] = useState(0);

  useEffect(() => {
    if (!leftSideRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setLeftSideHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(leftSideRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // 右側のタイムテーブルの高さを左側に合わせる
  const adjustedContainerHeight = Math.max(containerHeight, leftSideHeight);

  return (
    <div className='flex border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition'>
      {/* 左側: 陸送員名固定表示 */}
      <div
        ref={leftSideRef}
        className='w-32 sm:w-48 flex-shrink-0 bg-gray-50 dark:bg-gray-700 border-r-2 border-gray-300 dark:border-gray-600 sticky left-0 z-20'
      >
        <div className='px-3 sm:px-4 py-1'>
          <div className='mb-1'>
            <button
              onClick={() => onTransporterClick(transporter)}
              className='group block w-full text-left'
            >
              <div className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition'>
                {transporter.name}
              </div>
            </button>
          </div>

          {/* 特記事項タグ */}
          {transporterTags.length > 0 && (
            <div className='flex flex-wrap gap-1 mt-1'>
              {transporterTags.map(tag => (
                <span
                  key={tag.text}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${tag.className}`}
                >
                  {tag.text}
                </span>
              ))}
            </div>
          )}

          {/* 特記事項（テキスト） */}
          {transporter.notes && (
            <div className='text-xs text-gray-600 dark:text-gray-400 mt-1 truncate'>
              {transporter.notes}
            </div>
          )}
        </div>
      </div>

      {/* 右側: タイムテーブル */}
      <DroppableZone
        id={`transporter-${transporter.id}`}
        landTransporter={transporter}
        className='flex-1 relative'
      >
        <div>
          <div style={{ width: `${timeSlots.length * TIME_SLOT_WIDTH}px` }}>
            {/* 案件表示エリア */}
            <div className='relative' style={{ minHeight: `${adjustedContainerHeight}px` }}>
              {/* 背景の時間軸グリッド */}
              <div
                className='absolute top-0 left-0'
                style={{
                  display: 'flex',
                  width: `${timeSlots.length * TIME_SLOT_WIDTH}px`,
                }}
              >
                {timeSlots.map(timeSlot => {
                  const isWithinWorkingHours =
                    workingHours.startTime <= timeSlot && timeSlot < workingHours.endTime;

                  // 案件がこの時間帯にあるかチェック
                  const hasCaseInThisSlot = assignedCases.some(case_ => {
                    if (case_.pickupDate === selectedDate) {
                      if (case_.deliveryDate === selectedDate) {
                        return case_.pickupTimeStart <= timeSlot && timeSlot < case_.deliveryTime;
                      } else {
                        return case_.pickupTimeStart <= timeSlot;
                      }
                    }
                    return false;
                  });

                  return (
                    <DroppableZone
                      key={`bg-${timeSlot}`}
                      id={`transporter-${transporter.id}-${timeSlot}`}
                      landTransporter={transporter}
                      className={`border-r border-gray-200 dark:border-gray-700 last:border-r-0 ${
                        !isWithinWorkingHours
                          ? 'bg-gray-100 dark:bg-gray-700'
                          : 'bg-white dark:bg-gray-800'
                      }`}
                      style={{
                        width: `${TIME_SLOT_WIDTH}px`,
                        flexShrink: 0,
                        pointerEvents: hasCaseInThisSlot ? 'none' : 'auto',
                        maxHeight: `${adjustedContainerHeight}px`,
                        overflow: 'hidden',
                        zIndex: 10,
                      }}
                    >
                      <div style={{ height: `${adjustedContainerHeight}px` }}>
                        {!hasCaseInThisSlot && isWithinWorkingHours && (
                          <div
                            data-testid={`timeslot-${transporter.id}-${timeSlot}`}
                            className='h-full flex items-center justify-center text-center text-gray-400 dark:text-gray-500 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 border-2 border-dashed border-transparent hover:border-blue-300 dark:hover:border-blue-600 transition cursor-pointer'
                            onClick={() =>
                              onTimeSlotClick?.(transporter.id, timeSlot, selectedDate)
                            }
                          >
                            空き
                          </div>
                        )}
                        {!isWithinWorkingHours && (
                          <div className='h-full flex items-center justify-center text-center text-gray-300 dark:text-gray-600 text-xs'>
                            勤務時間外
                          </div>
                        )}
                      </div>
                    </DroppableZone>
                  );
                })}
              </div>

              {/* 案件ボタンを絶対配置で表示 */}
              {assignedCases.map(case_ => {
                const startTime = case_.pickupTimeStart;
                const endTime =
                  case_.deliveryDate === selectedDate ? case_.deliveryTime : workingHours.endTime;

                const casePosition = calculateCasePosition(startTime, endTime, timeSlots);

                if (casePosition.startIndex === -1 || casePosition.width <= 0) return null;

                const laneNumber = caseToLane.get(case_.id) || 0;
                const laneStyle = getLaneStyle(laneNumber);

                return (
                  <DraggableCase
                    key={case_.id}
                    case={case_}
                    onUnassign={onUnassignCase}
                    timeSlotHeight={20}
                    duration={1}
                    getVehicleName={getVehicleName}
                    getBillingPartnerName={getBillingPartnerName}
                    getPickupPartnerName={getPickupPartnerName}
                    getDeliveryPartnerName={getDeliveryPartnerName}
                    gridPosition={{
                      left: casePosition.startIndex * TIME_SLOT_WIDTH,
                      width: casePosition.width * TIME_SLOT_WIDTH,
                      top: parseInt(laneStyle.top),
                      height: 52,
                    }}
                  />
                );
              })}

              {/* プレビュー案件バー表示 */}
              {activeCase &&
                dragOverTransporter &&
                dragOverTransporter.id === transporter.id &&
                (() => {
                  const startTime = activeCase.pickupTimeStart;
                  const endTime =
                    activeCase.deliveryDate === selectedDate
                      ? activeCase.deliveryTime
                      : workingHours.endTime;

                  const previewPosition = calculateCasePosition(startTime, endTime, timeSlots);

                  if (previewPosition.startIndex === -1 || previewPosition.width <= 0) return null;

                  return (
                    <div
                      key={`preview-${activeCase.id}`}
                      className={`absolute border-2 border-dashed rounded-lg z-10 ${
                        canDrop
                          ? 'border-blue-400 bg-blue-100/50 dark:bg-blue-900/30'
                          : 'border-red-400 bg-red-100/50 dark:bg-red-900/30'
                      }`}
                      style={{
                        left: `${previewPosition.startIndex * TIME_SLOT_WIDTH}px`,
                        width: `${previewPosition.width * TIME_SLOT_WIDTH}px`,
                        top: '4px',
                        height: '52px',
                      }}
                    >
                      <div className='flex items-center justify-center h-full'>
                        {canDrop ? (
                          <div className='px-2 py-1 bg-blue-500/80 text-white text-xs rounded-full'>
                            プレビュー: {getVehicleName(activeCase)}
                          </div>
                        ) : (
                          <div className='flex items-center px-2 py-1 bg-red-500/90 text-white text-xs rounded-full'>
                            <span className='mr-1'>❌</span>
                            <span>{dropReason || '割り当て不可'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      </DroppableZone>
    </div>
  );
};
